import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { ExpansionPanel } from '../../../../../forms/form-components/expansion-panel/expansion-panel';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { Nullable } from '../../../../../shared-types';
import { getClaim } from '../../../../../claim';
import { ClaimType } from '../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    updateFormDetails?: UpdateFormDetails;
    disableEditButton?: boolean;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const doctorPracticeDetailsView: React.FunctionComponent<Props> = ({
    classes,
    formDetails,
    claimType,
}: Props) => {
    const medical = formDetails?.lifeAssured?.disabilityClaimConditions;
    const doctors = formDetails?.lifeAssured?.medicalInformation?.doctorVisits || [
        {
            doctorsName: null,
            doctorsContactDetails: null,
            practiceNumber: null,
            emailAddress: null,
            addressDetails: {
                addressType: null,
                addressLineTwo: null,
                postalCode: null,
                addressLineOne: null,
                addressSuburb: null,
                country: null,
            },
            occurences: [
                {
                    dateOfAttendance: null,
                    reason: null,
                },
            ],
        },
    ];
    const disabilityDoctors = formDetails?.lifeAssured?.disabilityClaimConditions?.doctorVisits || [
        {
            doctorVisits: [
                {
                    occurences: [
                        {
                            dateOfAttendance: null,
                            reason: null,
                        },
                    ],
                    addressDetails: {
                        addressType: null,
                        addressLineTwo: null,
                        postalCode: null,
                        addressLineOne: null,
                        addressSuburb: null,
                        country: null,
                    },
                    doctorsName: null,
                    doctorsContactNumber: null,
                },
            ],
            lifeAssuredMedicalReason: null,
            lifeAssuredDateOfDiagnosis: null,
            lifeAssuredMedicalCondition: null,
            lifeAssuredAccidentDetails: null,
            whatFormOfTreatmentOrMedicationAreYouCurrentlyTaking: null,
        },
    ];
    const pastDoctorVisit = formDetails?.lifeAssured?.retrenchmentClaimConditions?.pastDoctorVisit;

    const retrenchmentDoctors = formDetails?.lifeAssured?.retrenchmentClaimConditions
        ?.doctorVisits || [
        {
            doctorVisits: [
                {
                    addressDetails: {
                        addressType: null,
                        addressLineTwo: null,
                        postalCode: null,
                        addressLineOne: null,
                        addressSuburb: null,
                        country: null,
                    },
                    doctorsName: null,
                    doctorsContactNumber: null,
                    consultationDate: null,
                    consultationDetails: null,
                },
            ],
            pastDoctorVisit: null,
        },
    ];

    return (
        <React.Fragment>
            {claimType === ClaimType.Disability && (
                <>
                    <Grid container justify="space-between" className={classes.marginTop2}>
                        <Grid container item xs={10} alignItems="center">
                            <Typography
                                style={{ padding: 10 }}
                                className={classes.heading}
                                variant="body1"
                            >
                                Disability Claim Conditions
                            </Typography>
                        </Grid>
                    </Grid>
                    <Paper className={classes.paper}>
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="What is the medical reason for the claim being submitted?"
                                        value={
                                            medical?.lifeAssuredMedicalReason ||
                                            medical?.drLifeAssuredMedicalReason
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Please provide date of diagnosis or date that the event took place"
                                        value={
                                            getDisplayDate(medical?.lifeAssuredDateOfDiagnosis) ||
                                            getDisplayDate(medical?.drLifeAssuredDateOfDiagnosis)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Medical condition was acquired due to?"
                                        value={
                                            medical?.lifeAssuredMedicalCondition ||
                                            medical?.drLifeAssuredMedicalCondition
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="What form of treatment or medication are you currently taking?"
                                        value={
                                            medical?.whatFormOfTreatmentOrMedicationAreYouCurrentlyTaking ||
                                            medical?.drWhatFormOfTreatmentOrMedicationAreYouCurrentlyTaking
                                        }
                                    />
                                </Grid>
                                {medical?.lifeAssuredMedicalCondition !== 'Disease' && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label=" Please provide details of the accident. (Include case number, name of police station and attached police report if applicable)."
                                            value={
                                                medical?.lifeAssuredAccidentDetails ||
                                                medical?.drLifeAssuredAccidentDetails
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                    <Grid container justify="space-between" className={classes.marginTop2}>
                        <Grid container item xs={10} alignItems="center">
                            <Typography
                                style={{ padding: 10 }}
                                className={classes.heading}
                                variant="body1"
                            >
                                Doctor and practice details
                            </Typography>
                        </Grid>
                    </Grid>
                </>
            )}

            {disabilityDoctors.map((doctor: any, index: number) => (
                <Grid key={index}>
                    {claimType === ClaimType.Disability && (
                        <ExpansionPanel name={`Doctor ${index + 1}`}>
                            <Grid container justify="flex-start">
                                <Grid item xs={12} container justify="flex-start">
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Dr Name"
                                            value={doctor.doctorsName || doctor.drDoctorsName}
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Dr's contact number"
                                            value={
                                                doctor.doctorsContactNumber ||
                                                doctor.drDoctorsContactNumber
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Address"
                                            value={[
                                                doctor.addressDetails?.address,
                                                doctor.addressDetails?.suburb,
                                                doctor.addressDetails?.town,
                                                doctor.addressDetails?.postalCode,
                                                doctor.addressDetails?.province,
                                                doctor.addressDetails?.country,
                                            ]
                                                .filter(Boolean)
                                                .join('\n')}
                                        />
                                    </Grid>
                                </Grid>
                                {Array.isArray(doctor.occurences) &&
                                    doctor.occurences.length > 0 &&
                                    doctor.occurences.map(
                                        (
                                            occurence: {
                                                dateOfAttendance: Nullable<Date>;
                                                reason:
                                                    | boolean
                                                    | React.ReactChild
                                                    | React.ReactFragment
                                                    | React.ReactPortal
                                                    | React.ReactNode[]
                                                    | null
                                                    | undefined;
                                            },
                                            index: React.Key | null | undefined
                                        ) => (
                                            <Grid item container xs={12} key={index}>
                                                <Grid item xs={4} lg={3}>
                                                    <LabeledValue
                                                        label="Date of attendance"
                                                        value={getDisplayDate(
                                                            occurence.dateOfAttendance
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={4} lg={3}>
                                                    <LabeledValue
                                                        label="Reason for consultation"
                                                        value={occurence.reason}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )
                                    )}
                            </Grid>
                        </ExpansionPanel>
                    )}
                </Grid>
            ))}

            {doctors.map((doctor: any, index: number) => (
                <Grid key={index}>
                    {isDreadOrTerminal() && (
                        <ExpansionPanel name={`Doctor ${index + 1}`}>
                            <Grid container justify="flex-start">
                                <Grid item xs={12} container justify="flex-start">
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Doctor's name"
                                            value={doctor.doctorsName}
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Practice number"
                                            value={doctor.practiceNumber}
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Contact number"
                                            value={doctor.doctorsContactDetails}
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Email address"
                                            value={doctor.emailAddress}
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Address type"
                                            value={doctor.addressDetails?.addressType}
                                        />
                                    </Grid>

                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Address"
                                            value={`${
                                                doctor.addressDetails?.addressLineOne
                                            } ${'\n'} ${
                                                doctor?.addressDetails?.addressLineTwo === null
                                                    ? ''
                                                    : doctor.addressDetails?.addressLineTwo
                                            }  ${'\n'} ${
                                                doctor.addressDetails?.addressSuburb === null
                                                    ? ''
                                                    : doctor.addressDetails?.addressSuburb
                                            }
                                        ${'\n'} ${
                                                doctor.addressDetails?.addressTown === null
                                                    ? ''
                                                    : doctor.addressDetails?.addressTown
                                            }
                                        ${'\n'} ${
                                                doctor.addressDetails?.postalCode === null
                                                    ? ''
                                                    : doctor.addressDetails?.postalCode
                                            } ${'\n'} ${doctor.addressDetails?.country}`}
                                        />
                                    </Grid>
                                </Grid>
                                {Array.isArray(doctor.occurences) &&
                                    doctor.occurences.length > 0 &&
                                    doctor.occurences.map(
                                        (
                                            occurence: {
                                                dateOfAttendance: Nullable<Date>;
                                                reason:
                                                    | boolean
                                                    | React.ReactChild
                                                    | React.ReactFragment
                                                    | React.ReactPortal
                                                    | React.ReactNode[]
                                                    | null
                                                    | undefined;
                                            },
                                            index: React.Key | null | undefined
                                        ) => (
                                            <Grid item container xs={12} key={index}>
                                                <Grid item xs={4} lg={3}>
                                                    <LabeledValue
                                                        label="Date of attendance"
                                                        value={getDisplayDate(
                                                            occurence.dateOfAttendance
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={4} lg={3}>
                                                    <LabeledValue
                                                        label="Reason for consultation"
                                                        value={occurence.reason}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )
                                    )}
                            </Grid>
                        </ExpansionPanel>
                    )}
                </Grid>
            ))}

            {retrenchmentDoctors.map((doctor: any, index: number) => (
                <Grid key={index}>
                    {claimType === ClaimType.Retrenchment && (
                        <>
                            <Grid container justify="space-between" className={classes.marginTop2}>
                                <Grid container item xs={10} alignItems="center">
                                    <Typography
                                        style={{ padding: 10 }}
                                        className={classes.heading}
                                        variant="body1"
                                    >
                                        Doctor and practice details
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} lg={3} style={{ padding: 10 }}>
                                    <LabeledValue
                                        label="Have you seen the doctor in the past 5 years"
                                        value={pastDoctorVisit}
                                    />
                                </Grid>
                            </Grid>
                            <ExpansionPanel name={`Doctor ${index + 1}`}>
                                <Grid container justify="flex-start">
                                    <Grid item xs={12} container justify="flex-start">
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Consultation date"
                                                value={getDisplayDate(doctor.consultationDate)}
                                            />
                                        </Grid>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Please provide details of the consultation details"
                                                value={doctor.consultationDetails}
                                            />
                                        </Grid>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Doctor's name"
                                                value={doctor.doctorsName}
                                            />
                                        </Grid>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Doctor's contact number"
                                                value={doctor.doctorsContactDetails}
                                            />
                                        </Grid>

                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Address"
                                                value={`${
                                                    doctor.addressDetails?.addressLineOne
                                                } ${'\n'} ${
                                                    doctor?.addressDetails?.addressLineTwo === null
                                                        ? ''
                                                        : doctor.addressDetails?.addressLineTwo
                                                }  ${'\n'} ${
                                                    doctor.addressDetails?.addressSuburb === null
                                                        ? ''
                                                        : doctor.addressDetails?.addressSuburb
                                                }
                                        ${'\n'} ${
                                                    doctor.addressDetails?.addressTown === null
                                                        ? ''
                                                        : doctor.addressDetails?.addressTown
                                                }
                                        ${'\n'} ${
                                                    doctor.addressDetails?.postalCode === null
                                                        ? ''
                                                        : doctor.addressDetails?.postalCode
                                                } ${'\n'} ${doctor.addressDetails?.country}`}
                                            />
                                        </Grid>
                                    </Grid>
                                    {Array.isArray(doctor.occurences) &&
                                        doctor.occurences.length > 0 &&
                                        doctor.occurences.map(
                                            (
                                                occurence: {
                                                    dateOfAttendance: Nullable<Date>;
                                                    reason:
                                                        | boolean
                                                        | React.ReactChild
                                                        | React.ReactFragment
                                                        | React.ReactPortal
                                                        | React.ReactNode[]
                                                        | null
                                                        | undefined;
                                                },
                                                index: React.Key | null | undefined
                                            ) => (
                                                <Grid item container xs={12} key={index}>
                                                    <Grid item xs={4} lg={3}>
                                                        <LabeledValue
                                                            label="Date of attendance"
                                                            value={getDisplayDate(
                                                                occurence.dateOfAttendance
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4} lg={3}>
                                                        <LabeledValue
                                                            label="Reason for consultation"
                                                            value={occurence.reason}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )
                                        )}
                                </Grid>
                            </ExpansionPanel>
                        </>
                    )}
                </Grid>
            ))}
        </React.Fragment>
    );

    function isDreadOrTerminal() {
        return claimType === ClaimType.Dread || claimType === ClaimType.TerminalIllness;
    }
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails || getFormDetails(state),
    disableEditButton: ownProps.disableEditButton || false,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const DoctorPracticeDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(doctorPracticeDetailsView));
